import { useState, useEffect } from 'react';
import { Dashboard, api, useToast } from './_barrel.js';


        // =======================================================================
        // Dashboard
        // =======================================================================
        // ====================================================================
        // RunNowControl — reusable button + progress poll for triggering
        // a collection run. scope: 'all' | 'subject' | 'business'.
        // ====================================================================
        function RunNowControl({ token, scope, targetId, label }) {
            const [status, setStatus] = useState(null);
            const [error, setError] = useState(null);
            const [starting, setStarting] = useState(false);
            const toast = useToast();

            // Poll status while a run is in flight
            useEffect(() => {
                let timer = null;
                let cancelled = false;
                const poll = async () => {
                    try {
                        const s = await api('/api/v1/run-now/status', {}, token);
                        if (cancelled) return;
                        setStatus(s);
                        if (s.in_flight) {
                            timer = setTimeout(poll, 2000);
                        }
                    } catch (e) {
                        // Silently stop polling on error
                    }
                };
                poll();
                return () => { cancelled = true; if (timer) clearTimeout(timer); };
            }, [token, starting]);

            const start = async () => {
                if (status?.in_flight) {
                    if (!confirm('A collection run is already in flight. Wait for it to finish before starting another.')) return;
                    return;
                }
                let confirmMsg = 'Start a collection run now?';
                if (scope === 'all') {
                    confirmMsg = 'Run collection across all active subjects and businesses?\n\nSources: Google News, Bluesky, Mastodon (all free) + LLM classification. X (Twitter) is skipped unless X_DAILY_ENABLED is set on the server (it is paid, and this runs every subject).\n\nThis can take 5-15 minutes depending on counts.';
                }
                if (!confirm(confirmMsg)) return;

                setStarting(true); setError(null);
                try {
                    const r = await api('/api/v1/run-now', {
                        method: 'POST',
                        body: JSON.stringify({ scope, target_id: targetId || null }),
                    }, token);
                    toast(`Run ${r.run_id} started`);
                } catch (e) {
                    setError(e.message);
                } finally {
                    setStarting(false);
                }
            };

            const inFlight = status?.in_flight;
            const pct = (status && status.targets_total > 0)
                ? Math.round((status.targets_done / status.targets_total) * 100)
                : 0;

            return (
                <div style={{minWidth: scope === 'all' ? '320px' : '180px'}}>
                    <button
                        className={inFlight ? "btn-ghost" : "btn-gold"}
                        onClick={start}
                        disabled={starting || inFlight}
                        style={{width: '100%'}}>
                        {inFlight
                            ? `Running… ${status.targets_done}/${status.targets_total} (${pct}%)`
                            : (starting ? 'Starting…' : (label || '↻ Run Now'))}
                    </button>
                    {inFlight && status.last_message && (
                        <div className="text-xs font-mono text-muted mt-2 text-right">
                            {status.last_message}
                            {status.new_hostile > 0 && <div style={{color: 'var(--amber)'}}>
                                {status.new_hostile} hostile so far
                            </div>}
                        </div>
                    )}
                    {!inFlight && status?.last_message && status.targets_total > 0 && (
                        <div className="text-xs font-mono text-muted mt-2 text-right">
                            Last: {status.last_message}
                        </div>
                    )}
                    {error && <div className="text-xs mt-2" style={{color: 'var(--red)'}}>{error}</div>}
                </div>
            );
        }

export { RunNowControl };
